import { useState, useRef } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  UploadIcon,
  SpreadsheetIcon,
  CloseIcon,
  AlertIcon,
  SuccessIcon,
  SpinnerIcon,
} from "@/lib/icons";
import { TaskTemplateColumnView } from "./TaskTemplateColumnView";
import {
  type ValidationError,
  type ExcelData,
  validateExcelFile,
  importExcelData,
  isExcelFile,
  toPreviewPackages,
} from "./taskTemplateParser";

export type {
  Subtask,
  NormalTask,
  InspectionTask,
  DocumentTask,
  WorkSection,
  ProcurementPackage,
  ExcelData,
} from "./taskTemplateParser";

// ───────────────────────── Types ─────────────────────────

interface TaskTemplateProps {
  file: File | null;
  onChange: (file: File | null) => void;
  onDataImport?: (data: ExcelData) => void;
}

// ───────────────────────── Constants ─────────────────────────

const NORMAL_BG_HOVER_COLOR = "hover:bg-muted/40";
const ERROR_BANNER_CLASS = "border-destructive/50 bg-destructive/5";

// ───────────────────────── Component ─────────────────────────

export function TaskTemplateImportAndValidation({ file, onChange, onDataImport }: TaskTemplateProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [previewData, setPreviewData] = useState<ExcelData | null>(null);

  const resetState = () => {
    setFileError(null);
    setValidationErrors([]);
    setPreviewData(null);
  };

  const processFile = async (f: File) => {
    resetState();

    if (!isExcelFile(f)) {
      setFileError("Vain Excel-tiedostot ovat sallittuja (.xls, .xlsx, .xlsm, .xlt, .xltx).");
      return;
    }

    setIsProcessing(true);

    try {
      const errors = await validateExcelFile(f);
      console.log("Imported task template data validation errors:", errors);
      if (errors.length > 0) {
        setValidationErrors(errors);
      }

      const data = await importExcelData(f, errors);
      setPreviewData(data);
      onChange(f);
      onDataImport?.(data);
    } catch (err) {
      setFileError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsProcessing(false);
    }
  };

  // ── Drag handlers ──
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) setIsDragOver(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) processFile(droppedFile);
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) processFile(selected);
    e.target.value = "";
  };

  const totalTasks = previewData?.procurementPackages.reduce((s, pp) => s + pp.tasks.length, 0) ?? 0;
  const totalSubtasks = previewData?.procurementPackages.reduce(
    (s, pp) =>
      s +
      pp.tasks.reduce((s2, t) => s2 + t.subtasks.length, 0) +
      pp.workSections.reduce(
        (s2, ws) => s2 + ws.documentTasks.reduce((ds, dt) => ds + dt.subtasks.length, 0),
        0
      ),
    0
  ) ?? 0;
  const totalWorkSectionTasks = previewData?.procurementPackages.reduce(
    (s, pp) => s + pp.workSections.reduce((s2, ws) => s2 + ws.inspectionTasks.length + ws.documentTasks.length, 0), 0
  ) ?? 0;
  const totalWorkSections = previewData?.procurementPackages.reduce(
    (s, pp) => s + pp.workSections.length, 0
  ) ?? 0;

  return (
    <div className="space-y-3 min-w-0">
      {/* ── Drop zone ── */}
      {!file && (
        <div
          className={`relative rounded-xl border-2 border-dashed p-8 text-center transition-all duration-200 ${
            isDragOver
              ? "border-blue-500 bg-blue-50/60 scale-[1.01]"
              : `border-muted-foreground/25 bg-muted/30 hover:border-muted-foreground/40 ${NORMAL_BG_HOVER_COLOR}`
          }`}
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xls,.xlsx,.xlsm,.xlt,.xltx"
            className="hidden"
            onChange={handleInputChange}
          />
          <div className="flex flex-col items-center gap-3">
            {isProcessing ? (
              <SpinnerIcon className="h-10 w-10 text-muted-foreground animate-spin" />
            ) : (
              <UploadIcon className="h-10 w-10 text-muted-foreground/60" />
            )}
            <div>
              <p className="text-sm font-medium text-foreground">
                {isProcessing ? "Käsitellään tiedostoa…" : "Vedä ja pudota Excel-tiedosto tähän"}
              </p>
              {!isProcessing && (
                <p className="mt-1 text-xs text-muted-foreground">
                  tai{" "}
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    selaa tiedostoja
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── File chip ── */}
      {file && !fileError && (
        <div className="flex flex-col rounded-lg border bg-muted/40 px-2 py-2 overflow-hidden">
          <div className="flex items-center gap-2 w-full">
            <SpreadsheetIcon className="h-4 w-4 shrink-0 text-green-600" />
            <span className="truncate text-sm font-medium min-w-0 max-w-[600px] flex-1">{file.name}</span>
            <button
              type="button"
              onClick={() => {
                onChange(null);
                resetState();
              }}
              className="ml-auto shrink-0 rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
              title="Poista tiedosto"
            >
              <CloseIcon className="h-4 w-4" />
            </button>
          </div>
          {previewData && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1.5 text-xs text-muted-foreground">
              <span><span className="font-medium text-foreground">{previewData.procurementPackages.length}</span> hankintapakettia</span>
              <span className="text-muted-foreground/50">·</span>
              <span><span className="font-medium text-foreground">{totalTasks}</span> tehtävää</span>
              <span><span className="text-muted-foreground/70">({totalSubtasks} alitehtävää)</span></span>
              <span className="text-muted-foreground/50">·</span>
              <span><span className="font-medium text-foreground">{totalWorkSections}</span> työpakettia</span>
              <span><span className="text-muted-foreground/70">({totalWorkSectionTasks} työpakettitehtävää)</span></span>
            </div>
          )}
        </div>
      )}

      {/* ── Error banner ── */}
      {fileError && (
        <Card className={ERROR_BANNER_CLASS}>
          <CardContent className="flex items-start gap-3 py-3">
            <AlertIcon className="h-5 w-5 shrink-0 text-destructive mt-0.5" />
            <div className="space-y-1 min-w-0">
              <p className="text-sm font-medium text-destructive">{fileError}</p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-muted-foreground"
                onClick={() => {
                  onChange(null);
                  resetState();
                }}
              >
                Valitse toinen tiedosto
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Validation errors (informational) ── */}
      {validationErrors.length > 0 && (() => {
        const metaSheetNames = new Set(["Hankintapaketit", "Työvaiheet", "Määräajat", "Liitedokumentit", "(koko tiedosto)"]);
        const metaErrors = validationErrors.filter((e) => metaSheetNames.has(e.sheet));
        const totalErrors = validationErrors.reduce(
          (s, e) => s + e.packageErrors.length + Array.from(e.taskErrors.values()).reduce((sum, errs) => sum + errs.length, 0),
          0
        );
        return (
          <Card className={ERROR_BANNER_CLASS}>
            <CardHeader className="py-3 px-4">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <AlertIcon className="h-4 w-4 text-destructive" />
                Validointivirheitä ({totalErrors} kpl)
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3 pt-0 space-y-3">
              <p className="text-xs text-muted-foreground">
                Korjaa virheet ennen tuontia. Hankintapakettikohtaiset virheet näkyvät esikatselussa.
              </p>
              {metaErrors.map((e) => (
                <div key={e.sheet} className="space-y-1">
                  <div className="text-xs font-semibold text-foreground">{e.sheet}</div>
                  <ul className="list-disc list-inside space-y-0.5 text-xs text-destructive">
                    {e.packageErrors.map((err, i) => (
                      <li key={i} className="leading-tight">{err}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })()}

      {/* ── Preview table (expandable) ── */}
      {previewData && previewData.procurementPackages.length > 0 && (
        <Card className="overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between gap-3 py-3 px-4 border-b bg-muted/20">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              {validationErrors.length > 0
                ? <AlertIcon className="h-4 w-4 text-destructive" />
                : <SuccessIcon className="h-4 w-4 text-green-600" />}
              Esikatselu
            </CardTitle>
            <p className="text-[11px] text-muted-foreground">Valitse hankintapaketti vasemmalta</p>
          </CardHeader>
          <TaskTemplateColumnView packages={toPreviewPackages(previewData)} maxHeight="50vh" />
        </Card>
      )}
    </div>
  );
}
